import { create } from 'zustand';
import { AuthUser, Permission } from '@ikomet/shared';
import { post, get, tokenStore } from '@/lib/api';
import { setDisplayTimezone } from '@/lib/format';
import { useUiStore } from './uiStore';

interface AuthState {
  user: AuthUser | null;
  status: 'idle' | 'loading' | 'authenticated' | 'anonymous';
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  /** Takes a token pair issued elsewhere — the SSO callback. */
  adoptSession: (accessToken: string, refreshToken: string) => Promise<void>;
  logout: () => Promise<void>;
  restore: () => Promise<void>;
  /** Merges a fresh copy of the signed-in user, e.g. after a profile edit. */
  setUser: (patch: Partial<AuthUser>) => void;
  can: (permission: Permission) => boolean;
}

// There is no self-registration: accounts are created by invitation
// (see modules/invitations). Sessions begin at login or activation.
interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}


/**
 * Opens the workspace this person was last working in.
 *
 * The account remembers it, so a new device or a cleared cache still lands
 * where they left off rather than on whichever workspace sorts first. A local
 * value already matching is left alone — re-setting it would fire a needless
 * write back to the server through the store's own setter.
 */
const restoreWorkspace = (user: AuthUser) => {
  const remembered = user.lastWorkspaceId;
  if (!remembered) return;
  const { activeWorkspaceId, setActiveWorkspace } = useUiStore.getState();
  if (activeWorkspaceId === remembered) return;
  setActiveWorkspace(remembered);
};

export const useAuthStore = create<AuthState>((set, getState) => ({
  user: null,
  status: 'idle',
  error: null,

  login: async (email, password) => {
    set({ status: 'loading', error: null });
    try {
      const data = await post<AuthResponse>('/auth/login', { email, password });
      tokenStore.set(data.accessToken, data.refreshToken);
      // Applied here rather than in a component: formatting happens before the
      // first render, so anything later would flash the wrong hour.
      setDisplayTimezone(data.user.timezone);
      restoreWorkspace(data.user);
      set({ user: data.user, status: 'authenticated' });
    } catch (err) {
      set({ status: 'anonymous', error: (err as Error).message });
      throw err;
    }
  },

  /**
   * Adopts a session the API already issued.
   *
   * SSO completes on the API, so there are no credentials to post — the tokens
   * are stored and `/auth/me` confirms them. Fetching the user rather than
   * trusting the fragment means a tampered token fails here, at sign-in, rather
   * than on some later request with a half-built session in memory.
   */
  adoptSession: async (accessToken, refreshToken) => {
    set({ status: 'loading', error: null });
    tokenStore.set(accessToken, refreshToken);
    try {
      const user = await get<AuthUser>('/auth/me');
      setDisplayTimezone(user.timezone);
      restoreWorkspace(user);
      set({ user, status: 'authenticated' });
    } catch (err) {
      tokenStore.clear();
      set({ user: null, status: 'anonymous', error: (err as Error).message });
      throw err;
    }
  },

  logout: async () => {
    try {
      await post('/auth/logout', { refreshToken: tokenStore.refresh });
    } catch {
      // A failed logout call must not trap the user in the app.
    }
    tokenStore.clear();
    set({ user: null, status: 'anonymous' });
  },

  /** Re-hydrates the session on page load from the stored token. */
  restore: async () => {
    if (!tokenStore.access) {
      set({ status: 'anonymous' });
      return;
    }
    set({ status: 'loading' });
    try {
      const user = await get<AuthUser>('/auth/me');
      setDisplayTimezone(user.timezone);
      restoreWorkspace(user);
      set({ user, status: 'authenticated' });
    } catch {
      tokenStore.clear();
      set({ user: null, status: 'anonymous' });
    }
  },

  setUser: (patch) =>

    set((state) => (state.user ? { user: { ...state.user, ...patch } } : {})),


  can: (permission) => getState().user?.permissions.includes(permission) ?? false,
}));
